import React, { useState, useEffect, useCallback } from 'react';

/**
 * BookOfTheWeekCarousel — Sleek crossfade carousel for "Letters to Evelyn"
 * Glass-morphism, responsive, auto-play with witty rotating slides
 */

const BOOK_DATA = {
  title: "Letters to Evelyn",
  author: "John Selman",
  tagline: "A True Supernatural Thriller Comedy Memoir",
  amazonLink: "https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US",
  freeReadLink: "https://drive.google.com/file/d/1UhDwAs9VwUUoIinRNwnHFYZ0RlcvMBQI/view?usp=sharing",
};

const SLIDES = [
  {
    type: 'highlight',
    title: 'Featured Read',
    content: "Letters to Evelyn",
    subtitle: "A True Supernatural Thriller Comedy Memoir by John Selman",
    highlight: "19 Five-Star Reviews  |  BlueInk Notable Book Award  |  Optioned for Film",
    accent: '#047857'
  },
  {
    type: 'review',
    title: 'Reader Review',
    quote: "Truth is stranger than fiction! This book had me laughing so hard I cried. The comical side of it is exceedingly brilliant.",
    reviewer: "Verified Amazon Reader",
    accent: '#67e8f9'
  },
  {
    type: 'quote',
    title: 'From the Pages',
    quote: "More funny than Dave Chappelle or your money back! 50+ finely-crafted zany zingers inside!",
    tagline: "Can induce hurricane-force winds of laughter!",
    accent: '#fbbf24'
  },
  {
    type: 'review',
    title: 'What Readers Say',
    quote: "Never in my life did I think… and that's what makes it funny! People are laughing their butts off reading this!",
    reviewer: "Amazon Reviewer",
    accent: '#FEF08A'
  },
  {
    type: 'quote',
    title: 'Warning Label',
    quote: "Side effects include: spontaneous snort-laughing in public, missed bus stops, and waking the dog.",
    tagline: "If you don't laugh, check your pulse.",
    accent: '#34d399'
  },
  {
    type: 'facts',
    title: 'Quick Facts',
    facts: ["Optioned for Film", "19 Five-Star Reviews", "True Story", "50+ Witty Jokes", "Kindle & Paperback"],
    accent: '#f59e0b'
  },
  {
    type: 'review',
    title: 'Why Readers Love It',
    quote: "Like if Stephen King and Jerry Seinfeld had a literary baby. Part thriller, part comedy, entirely unforgettable.",
    reviewer: "Verified Purchaser",
    accent: '#047857'
  }
];

const BookOfTheWeekCarousel = ({ autoPlay = true, interval = 6000 }) => {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 640);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const goTo = useCallback((idx) => {
    if (idx === current) return;
    setVisible(false);
    setTimeout(() => { setCurrent(idx); setVisible(true); }, 280);
  }, [current]);

  const next = useCallback(() => goTo((current + 1) % SLIDES.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + SLIDES.length) % SLIDES.length), [current, goTo]);

  useEffect(() => {
    if (!autoPlay || paused) return;
    const t = setInterval(next, interval);
    return () => clearInterval(t);
  }, [autoPlay, paused, interval, next]);

  const slide = SLIDES[current];
  const pad = isMobile ? '18px 16px' : '28px 32px';
  const minH = isMobile ? 160 : 200;
  const arrowSize = isMobile ? 28 : 32;
  const sidePad = isMobile ? 30 : 36;

  return (
    <div
      data-testid="book-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(22px)',
        borderRadius: isMobile ? 12 : 18,
        padding: pad,
        marginBottom: isMobile ? 12 : 18,
        position: 'relative', overflow: 'hidden', minHeight: minH,
        border: `1px solid ${slide.accent}18`,
        transition: 'border-color 0.5s ease',
        boxShadow: `0 0 70px ${slide.accent}08`
      }}
    >
      {/* Accent glow */}
      <div style={{
        position: 'absolute', top: -70, right: -70,
        width: isMobile ? 140 : 220, height: isMobile ? 140 : 220,
        borderRadius: '50%', background: `${slide.accent}0a`, filter: 'blur(55px)',
        transition: 'background 0.5s', pointerEvents: 'none'
      }} />

      {/* Nav arrows */}
      <button onClick={prev} data-testid="carousel-prev" style={{
        position: 'absolute', left: isMobile ? 4 : 8, top: '50%', transform: 'translateY(-50%)',
        background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(203, 213, 225, 0.8)',
        borderRadius: 8, width: arrowSize, height: arrowSize, color: '#475569',
        fontSize: isMobile ? '0.85rem' : '1rem', cursor: 'pointer', zIndex: 10, display: 'flex',
        alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', padding: 0
      }}>&#8249;</button>
      <button onClick={next} data-testid="carousel-next" style={{
        position: 'absolute', right: isMobile ? 4 : 8, top: '50%', transform: 'translateY(-50%)',
        background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(203, 213, 225, 0.8)',
        borderRadius: 8, width: arrowSize, height: arrowSize, color: '#475569',
        fontSize: isMobile ? '0.85rem' : '1rem', cursor: 'pointer', zIndex: 10, display: 'flex',
        alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', padding: 0
      }}>&#8250;</button>

      {/* Slide content */}
      <div style={{
        position: 'relative', zIndex: 5, paddingLeft: sidePad, paddingRight: sidePad,
        opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.28s ease, transform 0.28s ease'
      }}>
        <div style={{
          fontSize: isMobile ? '0.52rem' : '0.58rem', color: 'rgba(255,255,255,0.3)',
          textTransform: 'uppercase', letterSpacing: isMobile ? 2 : 3,
          marginBottom: isMobile ? 8 : 12, fontWeight: 600
        }}>{slide.title}</div>

        {slide.type === 'highlight' && (
          <div>
            <h2 style={{ color: slide.accent, fontSize: isMobile ? '1.2rem' : '1.6rem', fontWeight: 800, margin: '0 0 8px 0', letterSpacing: '-0.02em' }}>{slide.content}</h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: isMobile ? '0.78rem' : '0.88rem', margin: '0 0 12px 0' }}>{slide.subtitle}</p>
            <span style={{ background: `${slide.accent}12`, border: `1px solid ${slide.accent}22`, padding: isMobile ? '4px 10px' : '5px 14px', borderRadius: 8, color: slide.accent, fontWeight: 600, fontSize: isMobile ? '0.62rem' : '0.72rem', display: 'inline-block' }}>{slide.highlight}</span>
          </div>
        )}

        {slide.type === 'review' && (
          <div>
            <blockquote style={{ color: 'rgba(255,255,255,0.85)', fontSize: isMobile ? '0.85rem' : '0.98rem', fontStyle: 'italic', margin: '0 0 12px 0', lineHeight: 1.6, borderLeft: `3px solid ${slide.accent}40`, paddingLeft: isMobile ? 12 : 16 }}>"{slide.quote}"</blockquote>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: isMobile ? '0.62rem' : '0.72rem', margin: 0 }}>&mdash; {slide.reviewer}</p>
          </div>
        )}

        {slide.type === 'quote' && (
          <div>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: isMobile ? '0.9rem' : '1.05rem', fontWeight: 600, margin: '0 0 10px 0', lineHeight: 1.5 }}>{slide.quote}</p>
            <p style={{ color: `${slide.accent}90`, fontSize: isMobile ? '0.72rem' : '0.82rem', fontStyle: 'italic', margin: 0 }}>{slide.tagline}</p>
          </div>
        )}

        {slide.type === 'facts' && (
          <div style={{ display: 'flex', gap: isMobile ? 8 : 10, flexWrap: 'wrap' }}>
            {slide.facts.map((f, i) => (
              <span key={i} style={{
                background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(255,255,255,0.06)',
                padding: isMobile ? '5px 12px' : '7px 16px', borderRadius: 8,
                color: '#1e293b', fontSize: isMobile ? '0.72rem' : '0.82rem', fontWeight: 500
              }}>{f}</span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div style={{ marginTop: isMobile ? 12 : 16 }}>
          <a
            href={BOOK_DATA.amazonLink} target="_blank" rel="noopener noreferrer"
            data-testid="carousel-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: `${slide.accent}18`, border: `1px solid ${slide.accent}30`,
              color: slide.accent, padding: isMobile ? '7px 16px' : '9px 20px', borderRadius: 10,
              fontWeight: 700, fontSize: isMobile ? '0.7rem' : '0.78rem', textDecoration: 'none',
              transition: 'background 0.2s, transform 0.2s'
            }}
          >
            <i className="fas fa-book-open" style={{ fontSize: '0.65rem' }} />
            Get the Book
          </a>
        </div>
      </div>

      {/* Indicators */}
      <div style={{
        position: 'absolute', bottom: isMobile ? 8 : 12, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: isMobile ? 5 : 6, zIndex: 10
      }}>
        {SLIDES.map((s, i) => (
          <button key={i} onClick={() => goTo(i)} data-testid={`carousel-dot-${i}`} style={{
            width: i === current ? (isMobile ? 18 : 22) : 6, height: 6, borderRadius: 3, border: 'none',
            background: i === current ? s.accent : 'rgba(255,255,255,0.15)',
            cursor: 'pointer', transition: 'all 0.3s', padding: 0
          }} />
        ))}
      </div>
    </div>
  );
};

export default BookOfTheWeekCarousel;
